/**
 * Map-related view-formatting helpers (embed/directions URLs, overlay text).
 *
 * These are presentation-only concerns, so they're typed against the presentation `Vm`
 * types rather than the domain models, and live under `presentation/` rather than the
 * feature-root `utils/` (which is reserved for helpers shared across features/layers,
 * e.g. `storeListUtils.ts`'s `getCityList`/`findCityKey` are consumed by `plp` too).
 */
import type { NearestPhysicalStoreInfoVm, PhysicalStoreVm } from '../viewModels/physicalStore.vm';

const GOOGLE_MAPS_EMBED_KEY = import.meta.env.VITE_GOOGLE_MAPS_EMBED_KEY?.trim() ?? '';

function buildPlaceSearchUrl(destinationPlaceId: string): string {
  return `https://www.google.com/maps/search/?api=1&query=BlueStone+Jewellery&query_place_id=${encodeURIComponent(destinationPlaceId)}`;
}

function hasValidCoords(latitude: number, longitude: number): boolean {
  return (
    typeof latitude === 'number' &&
    typeof longitude === 'number' &&
    latitude !== 0 &&
    longitude !== 0
  );
}

export function usesGooglePlaceEmbedApi(url: string | null): boolean {
  return Boolean(url?.includes('/maps/embed/v1/place'));
}

export function buildMapEmbedUrl(store: PhysicalStoreVm): string | null {
  const { latitude, longitude, destinationPlaceId } = store;

  if (GOOGLE_MAPS_EMBED_KEY && destinationPlaceId) {
    return `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(GOOGLE_MAPS_EMBED_KEY)}&q=place_id:${encodeURIComponent(destinationPlaceId)}`;
  }

  if (hasValidCoords(latitude, longitude)) {
    return `https://www.google.com/maps?q=${latitude},${longitude}&hl=en&z=15&output=embed`;
  }

  return null;
}

export function buildMapPlaceUrl(store: PhysicalStoreVm): string | null {
  if (store.destinationPlaceId) {
    return buildPlaceSearchUrl(store.destinationPlaceId);
  }
  if (store.address.trim()) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(store.address)}`;
  }
  return null;
}

export function buildMapDirectionsUrl(store: PhysicalStoreVm): string | null {
  if (store.destinationPlaceId) {
    // Live store.html "Get Direction" uses Maps search + query_place_id (not maps/dir).
    return buildPlaceSearchUrl(store.destinationPlaceId);
  }
  if (hasValidCoords(store.latitude, store.longitude)) {
    return `https://www.google.com/maps/search/?api=1&query=${store.latitude},${store.longitude}`;
  }
  if (store.address.trim()) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(store.address)}`;
  }
  return null;
}

function truncateText(value: string, maxLength: number): string {
  const trimmed = value.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength - 1).trimEnd()}…`;
}

export function getMapOverlayTitle(store: PhysicalStoreVm): string {
  const storeLabel = store.storeName.trim().split(',')[0]?.trim() || store.storeName.trim();
  return truncateText(`BlueStone Jewellery ${storeLabel}`, 32);
}

export function getMapOverlayAddress(store: PhysicalStoreVm): string {
  return truncateText(store.address.trim(), 42);
}

/**
 * Same "place id, else lat/lng" URL-resolution logic as `buildMapPlaceUrl`/
 * `buildMapDirectionsUrl` above, adapted for the store-location widget's
 * `NearestPhysicalStoreInfoVm` shape (lat/lng as strings, no address fallback).
 * Centralized here instead of being redefined inline in `StoreLocationWidget.tsx`.
 */
export function buildNearestStoreMapsUrl(nearestStore: NearestPhysicalStoreInfoVm): string {
  if (nearestStore.destinationPlaceId) {
    return buildPlaceSearchUrl(nearestStore.destinationPlaceId);
  }
  if (nearestStore.latitude && nearestStore.longitude) {
    return `https://www.google.com/maps/search/?api=1&query=${nearestStore.latitude},${nearestStore.longitude}`;
  }
  return '';
}
